import { api } from '@api'
import { darkmode } from '@components'
import DOMPurify from 'dompurify'
import 'media-chrome';
import { type JSX, createResource } from 'solid-js'
import { Dynamic } from 'solid-js/web'
import { css } from 'styled-system/css'
import { ZoomableImage } from '../../../../custom-components/ZoomableImage'
import { AudioPlayer } from './AudioPlayer'

declare module 'solid-js' {
  namespace JSX {
    interface IntrinsicElements {
      'media-controller': any
      'media-time-display': any
      'media-duration-display': any
      'media-play-button': any
      'media-mute-button': any
      'media-volume-range': any
      'media-time-range': any
      'media-playback-rate-button': any
      'media-seek-backward-button': any
      'media-seek-forward-button': any
    }
  }
}

const signedUrlCache = new Map<string, { url: string; expiresAt: number }>()
const SIGNED_URL_TTL_SECS = 60 * 60

function ImageWrapper(props: { src: string; alt: string }): JSX.Element {
  return (
    <ZoomableImage src={props.src}>
      <img
        src={props.src}
        alt={props.alt}
      />
    </ZoomableImage>
  )
}

type RichTextProps = {
  content: string
  class?: string
}

function autoLinkUrls(html: string): string {
  const urlRegex = /(?<!["'=])\b(https?:\/\/[^\s<>"']+)/g
  return html.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
  })
}

function embedTradingViewCharts(raw: string): string {
  const withLinks = autoLinkUrls(raw)
  return withLinks.replace(
    /<a[^>]+href="(https:\/\/www\.tradingview\.com\/x\/([a-zA-Z0-9]+)\/?)"[^>]*>[^<]*<\/a>/g,
    (_, fullUrl, chartId) => {
      const imgSrc = `https://www.tradingview.com/x/${chartId}?png`
      return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer">${fullUrl}</a>
        <img src="${imgSrc}" alt="TradingView chart" />`
    },
  )
}

async function enhanceWithSignedUrls(html: string): Promise<string> {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  const imgTags = Array.from(doc.querySelectorAll('img'))
  const audioTags = Array.from(doc.querySelectorAll('audio'))
  const now = Date.now()

  const processTag = async (el: HTMLImageElement | HTMLAudioElement) => {
    const src = el.getAttribute('src')
    if (
      !src ||
      src.startsWith('data:') ||
      src.startsWith('blob:') ||
      src.includes('token=') ||
      src.includes('tradingview.com/x/')
    ) {
      return
    }
    const cached = signedUrlCache.get(src)
    if (cached && cached.expiresAt > now) {
      el.setAttribute('src', cached.url)
      return
    }

    const { data, error } = await api()
      .storage.from('channel-files')
      .createSignedUrl(src, SIGNED_URL_TTL_SECS)

    if (error) {
      console.warn(`Failed to sign URL for: ${src}`, error)
      return
    }

    if (data?.signedUrl) {
      signedUrlCache.set(src, {
        url: data.signedUrl,
        expiresAt: now + SIGNED_URL_TTL_SECS * 1000,
      })
      el.setAttribute('src', data.signedUrl)
    }
  }

  await Promise.all([...imgTags, ...audioTags].map(processTag))
  return doc.body.innerHTML
}

function convertHtmlToJsx(html: string): JSX.Element[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const body = doc.body

  function walk(node: ChildNode): JSX.Element | string | null {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent
    if (node.nodeType !== Node.ELEMENT_NODE) return null

    const el = node as HTMLElement
    const children = Array.from(el.childNodes).map(walk).filter(Boolean)

    if (el.tagName.toLowerCase() === 'img') {
      const src = el.getAttribute('src') ?? ''
      const alt = el.getAttribute('alt') ?? ''
      return <ImageWrapper src={src} alt={alt} />
    }

    if (el.tagName.toLowerCase() === 'audio') {
      const props = Object.fromEntries([...el.attributes].map((a) => [a.name, a.value]))
      return <AudioPlayer {...props} />
    }

    const tag = el.tagName.toLowerCase()
    const props = Object.fromEntries(
      [...el.attributes].map((attr) => [attr.name, attr.value]),
    )

    return (
      <Dynamic component={tag} {...props}>
        {children}
      </Dynamic>
    )
  }

  return Array.from(body.childNodes).map(walk).filter(Boolean) as JSX.Element[]
}

export default function RichText(props: RichTextProps): JSX.Element {
  const [enhancedHtml] = createResource(() => {
    const withAutoLinkedUrls = autoLinkUrls(props.content)
    const withEmbeddedTradingView = embedTradingViewCharts(withAutoLinkedUrls)

    return DOMPurify.sanitize(withEmbeddedTradingView, {
      USE_PROFILES: { html: true },
      // ✨ Whitelist data-user-id so it survives sanitization
      ADD_ATTR: ['target', 'rel', 'data-user-id'],
    })
  }, enhanceWithSignedUrls)

  return (
    <div
      class={css({
        fontSize: 'sm',
        lineHeight: '1.7',
        '& h1': { fontSize: 'lg', },
        '& strong': { fontWeight: 'bold' },
        '& a': { color: darkmode() ? 'jade.10' : 'jade.8' },
        '& em': { fontStyle: 'italic' },
        '& u': { textDecoration: 'underline' },
        '& ol': { listStyleType: 'decimal', paddingLeft: '6', marginBottom: '3' },
        '& ul': { listStyleType: 'disc', paddingLeft: '6', marginBottom: '3', marginTop: '3' },
        '& li': { marginBottom: '0' },
        '& img': { maxW: '200px' },

        // ✨ Setup Update Log Styles (Adjust visual tokens to match your app)
        '& .setup-update-change-message': {
          borderLeft: '1px solid',
          borderColor: darkmode() ? 'jade.10' : 'jade.10',
          paddingLeft: '3',
          marginTop: '2',
          marginBottom: '2',
          opacity: 0.9,
        },
        '& .update-header': {
          fontWeight: 'bold',
          marginBottom: '1',
          color: darkmode() ? 'jade.10' : 'jade.8',
        },
        '& .update-list': {
          listStyleType: 'none',
          paddingLeft: '0',
          margin: '0',
          display: 'flex',
          flexDirection: 'column',
          gap: '2',
        },
        '& .update-row': {
          fontSize: 'sm',
        }
      })}
    >
      {enhancedHtml.latest ? convertHtmlToJsx(enhancedHtml.latest) : null}
    </div>
  )
}
